/**
 * The composed share picture for a listing, as an og:image entry.
 *
 * Sharetribe's own `facebook` variant is a `crop=edges` 1200x630 that lands
 * on a hem and the floor as often as on the garment, and its imgix URLs are
 * signed, so the crop cannot be tuned. `/api/og-image` composes the picture
 * instead (server/api-util/og-image.js). The listing page uses this for the
 * listing itself; the profile page uses it with the seller's newest listing,
 * so a shared profile shows something they are selling rather than the
 * marketplace's generic picture.
 */

// Bump when server/api-util/og-image.js changes how the picture is composed.
// It only busts the cache; the server ignores it.
export const SHARE_IMAGE_REVISION = 2;

export const SHARE_IMAGE_WIDTH = 1200;
export const SHARE_IMAGE_HEIGHT = 630;

/**
 * @param {string} marketplaceRootURL
 * @param {Object} listing - needs `id.uuid` and `images[0].id.uuid`
 * @returns {Object|null} an og:image entry, or null when there is no photo
 */
export const composedShareImage = (marketplaceRootURL, listing) => {
  const listingId = listing?.id?.uuid;
  const firstImageId = listing?.images?.[0]?.id?.uuid;
  if (!listingId || !firstImageId) {
    return null;
  }
  return {
    name: 'og-image',
    // Keyed on the photo's id, so replacing the photo changes this URL rather
    // than waiting out the day-long edge cache — and on SHARE_IMAGE_REVISION,
    // so changing how the picture is composed does too. Without the second
    // half, a change to og-image.js leaves every already-cached card showing
    // the old crop for 24 hours, which is exactly when someone is looking at
    // it to review the change.
    url: `${marketplaceRootURL}/api/og-image/${listingId}.jpg?v=${firstImageId}&r=${SHARE_IMAGE_REVISION}`,
    width: SHARE_IMAGE_WIDTH,
    height: SHARE_IMAGE_HEIGHT,
  };
};

/**
 * Undefined rather than an empty array when there is nothing to show: Page
 * only falls back to the marketplace's own share image when the prop is
 * absent, and an empty array would leave the card with no image at all.
 */
export const withMarketplaceFallback = images =>
  images && images.length > 0 ? images : undefined;
